import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

import { Avatar } from '~/components/admin/Avatar';
import { chatVisitor, type VisitorState } from '~/lib/chat-visitor';
import { ChatAvailableForm, ChatProbing, ChatUnavailable } from './ChatAvailability';
import { ChatActive, ChatEnded, ChatWaiting } from './ChatConversation';
import { ChatFallbackForm, ChatFallbackSent } from './ChatFallbackForm';

export const PANEL_ID = 'kb-chat-panel';

/** Toestanden die een formulier tonen in plaats van een gesprek. Die krijgen
 *  één gedeelde scrollromp; een gesprek regelt zijn eigen indeling. */
const FORM_PHASES = new Set<VisitorState['phase']>([
  'probing', 'starting', 'available', 'unavailable', 'fallback', 'sent',
]);
const isFormPhase = (phase: VisitorState['phase']) => FORM_PHASES.has(phase);

/** De kop verandert mee met de toestand, zodat de bezoeker altijd ziet waar hij
 *  staat zonder het paneel te hoeven lezen. */
function heading(state: VisitorState): { title: string; sub: string } {
  switch (state.phase) {
    case 'waiting': return { title: 'Even geduld', sub: 'We zoeken een medewerker' };
    case 'chatting': return { title: state.session?.agentName ?? 'Support', sub: 'In gesprek' };
    case 'ended': return { title: 'Gesprek afgesloten', sub: 'Bedankt voor je bericht' };
    case 'fallback': return { title: 'Laat een bericht achter', sub: 'We reageren per e-mail' };
    case 'sent': return { title: 'Verstuurd', sub: 'Bedankt voor je bericht' };
    case 'unavailable': return { title: 'Contact', sub: 'Laat een bericht achter' };
    default: return { title: 'Chat met LEZA', sub: 'Support' };
  }
}

export function ChatPanel({ state }: { state: VisitorState }) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const { title, sub } = heading(state);

  // Focus naar het eerste zinvolle veld. De zes regels zijn overgenomen uit
  // Dialog.tsx:22-31 — maar Dialog zelf wordt bewust NIET geïmporteerd, zie de
  // toelichting bij role="dialog" hieronder.
  useEffect(() => {
    const box = panelRef.current;
    if (!box) return;
    const target = box.querySelector<HTMLElement>(
      'input:not([tabindex="-1"]), textarea:not([tabindex="-1"]), button:not([aria-label="Chat sluiten"])',
    );
    (target ?? box).focus({ preventScroll: true });
  }, [state.phase]);

  // Escape sluit het paneel, maar beëindigt het gesprek niet.
  //
  // Dialog.tsx:60 registreert zijn keydown op document in de CAPTURE-fase en
  // laat alleen de bovenste dialoog uit OPEN_DIALOGS reageren. Het chatpaneel
  // staat niet in die stapel, dus met een echte dialoog of de ImageLightbox
  // erbovenop zouden er twee tegelijk op één Escape reageren. Vandaar deze
  // controle op een open modaal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[aria-modal="true"]')) return;
      chatVisitor.close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      ref={panelRef}
      id={PANEL_ID}
      // role="dialog" ZONDER aria-modal, en dus zonder focus-trap.
      //
      // Een supportchat moet je laten doorlezen terwijl je op antwoord wacht.
      // Dialog.tsx is modaal (focus-trap, scroll-lock, backdrop) en dat zou de
      // widget vijandig maken: je zou het artikel waar je vraag over gaat niet
      // meer kunnen bekijken.
      role="dialog"
      aria-label="Chat met LEZA support"
      tabIndex={-1}
      className="fixed inset-x-0 bottom-0 z-200 flex max-h-[min(34rem,85svh)] flex-col overflow-hidden rounded-t-2xl border border-hairline bg-surface shadow-xl kb-no-print motion-safe:animate-fade-up sm:inset-x-auto sm:right-5 sm:bottom-24 sm:w-[23rem] sm:rounded-2xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* surface-glass alleen op de KOP: theme.css:541 beperkt die klasse tot
          "topbalk, dock, palet, modal-kop". De bellen eronder zijn kaarten. */}
      <header className="surface-glass flex items-center justify-between gap-3 border-b border-hairline px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {/* Het gezicht van de medewerker, zodra er een gesprek loopt. De
              Avatar-component valt terug op initialen als er geen foto is, dus
              de kop springt niet van vorm zodra iemand zonder foto opneemt. */}
          {state.session?.agentName && (state.phase === 'chatting' || state.phase === 'ended') && (
            <Avatar
              person={{ username: state.session.agentName, avatar: state.session.agentAvatar }}
              size={32}
            />
          )}
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-ink">{title}</p>
            <p className="truncate text-sm text-ink-3">{sub}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => chatVisitor.close()}
          aria-label="Chat sluiten"
          className="shrink-0 rounded-md p-1.5 text-ink-2 transition hover:bg-surface-hover hover:text-ink"
        >
          <X size={18} aria-hidden />
        </button>
      </header>

      {/* De formuliertoestanden krijgen één scrollbare romp.
          min-h-0 is hier DRAGEND: een flex-kind heeft standaard min-height:auto
          en weigert dan te krimpen, waardoor overflow-y-auto niets doet en de
          verstuurknop op een kleiner scherm buiten beeld valt zonder dat je er
          bij kunt. tabIndex maakt hem met het toetsenbord scrollbaar — axe's
          scrollable-region-focusable zit in de wcag2a-set van verify-a11y. */}
      {isFormPhase(state.phase) && (
        <div className="min-h-0 flex-1 overflow-y-auto" tabIndex={0}>
          {(state.phase === 'probing' || state.phase === 'starting') && <ChatProbing />}

          {state.phase === 'available' && state.availability && (
            <ChatAvailableForm availability={state.availability} busy={state.busy} error={state.error} />
          )}

          {state.phase === 'unavailable' && (
            <ChatUnavailable availability={state.availability} busy={state.busy} error={state.error} />
          )}

          {state.phase === 'fallback' && (
            <>
              <p className="border-b border-hairline bg-warning-soft px-4 py-3 text-sm text-warning">
                Er kwam helaas geen medewerker vrij. Laat je gegevens achter, dan reageren we per e-mail.
              </p>
              <ChatFallbackForm
                initialName={state.session?.customerName ?? ''}
                busy={state.busy}
                error={state.error}
              />
            </>
          )}

          {state.phase === 'sent' && <ChatFallbackSent />}
        </div>
      )}

      {/* De gesprektoestanden regelen hun eigen indeling: het transcript scrollt,
          het invoerveld blijft staan. */}
      {state.phase === 'waiting' && <ChatWaiting state={state} />}
      {state.phase === 'chatting' && <ChatActive state={state} />}
      {state.phase === 'ended' && <ChatEnded state={state} />}
    </div>
  );
}
